// ═══════════════════════════════════════════════════════════════════════════════
// OpenStreetMap embed preview + Google Maps outbound link (no API keys).
// Uses official embed — more reliable than third-party static PNG renderers.
// Embed is interactive (pan/zoom) when shown in an iframe without pointer-events: none.
// ═══════════════════════════════════════════════════════════════════════════════

/** Half-span in degrees; smaller = tighter street-level preview (~140 m half-span at HK latitude) */
const BBOX_HALF_DEG = 0.0012;

/** True when lat/lng are finite numbers within WGS84 ranges (for OSM embed / links). */
export const isValidGpsCoordinatePair = (lat: unknown, lng: unknown): boolean => {
  if (typeof lat !== 'number' || typeof lng !== 'number') {
    return false;
  }
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return false;
  }
  return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
};

/**
 * Official OSM embed URL (interactive inside iframe: pan/zoom per openstreetmap.org UI).
 * bbox order: min_lon, min_lat, max_lon, max_lat
 * marker: lat,lng
 */
export const buildOsmEmbedPreviewUrl = (lat: number, lng: number): string => {
  const minLon = lng - BBOX_HALF_DEG;
  const minLat = lat - BBOX_HALF_DEG;
  const maxLon = lng + BBOX_HALF_DEG;
  const maxLat = lat + BBOX_HALF_DEG;
  const bbox = `${minLon},${minLat},${maxLon},${maxLat}`;
  return `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(bbox)}&layer=mapnik&marker=${encodeURIComponent(`${lat},${lng}`)}`;
};

/** Google Maps (web or app handoff); no API key */
export const buildGoogleMapsUrl = (lat: number, lng: number): string =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat},${lng}`)}`;
